import React, { useEffect, useState, useRef } from 'react';
import { toolboxService } from '../../../services/api';

const Step2_Scraping = ({ urls, clid, onComplete, onBack, loading, error }) => {
  const [progress, setProgress] = useState({});
  const [results, setResults] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel ongoing requests if component unmounts
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (urls.length > 0 && !isProcessing) {
      startScraping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScraping = async () => {
    if (isProcessing) return; // Prevent multiple simultaneous runs

    setIsProcessing(true);
    const newResults = [];
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Process all URLs in parallel with proper error handling
    // Use requestAnimationFrame + setTimeout to ensure this runs in the next event loop tick (non-blocking)
    requestAnimationFrame(() => {
      setTimeout(async () => {
        // Batch progress updates to reduce re-renders
        const progressUpdates = {};
        urls.forEach((url, index) => {
          progressUpdates[url] = { status: 'processing', index: index + 1, total: urls.length };
        });

        // Single batch update instead of multiple individual updates
        if (isMountedRef.current) {
          setProgress(prev => ({ ...prev, ...progressUpdates }));
        }

        const scrapingPromises = urls.map(async (url, index) => {
          if (!isMountedRef.current || signal.aborted) return null;

          try {
            // Call API to scrape doctor info with cancellation support (non-blocking)
            const data = await toolboxService.scrapeDoctorInfo(url, clid, signal);

            if (!isMountedRef.current || signal.aborted) return null;

            // Handle cancellation
            if (data.cancelled) {
              return null;
            }

            if (data.success && data.data) {
              // data.data is now an array of doctors
              const doctorsList = Array.isArray(data.data) ? data.data : [data.data];

              if (doctorsList.length > 0 && doctorsList[0].doctor && doctorsList[0].doctor.primary_name) {
                // Update progress
                setTimeout(() => {
                  if (isMountedRef.current && !signal.aborted) {
                    setProgress(prev => ({
                      ...prev,
                      [url]: { status: 'success', index: index + 1, total: urls.length }
                    }));
                  }
                }, 0);

                // Return array of doctor data objects
                return {
                  url,
                  data: doctorsList,  // Now an array
                  success: true
                };
              } else {
                const errorMsg = 'No doctor information extracted from the page';
                setTimeout(() => {
                  if (isMountedRef.current && !signal.aborted) {
                    setProgress(prev => ({
                      ...prev,
                      [url]: { status: 'error', index: index + 1, total: urls.length, error: errorMsg }
                    }));
                  }
                }, 0);
                return {
                  url,
                  data: null,
                  success: false,
                  error: errorMsg
                };
              }
            } else {
              const errorMsg = data.error || 'Failed to scrape - no data returned';
              setTimeout(() => {
                if (isMountedRef.current && !signal.aborted) {
                  setProgress(prev => ({
                    ...prev,
                    [url]: { status: 'error', index: index + 1, total: urls.length, error: errorMsg }
                  }));
                }
              }, 0);
              return {
                url,
                data: null,
                success: false,
                error: errorMsg
              };
            }
          } catch (err) {
            if (!isMountedRef.current || signal.aborted) return null;
            const errorMsg = err.response?.data?.detail || err.message || 'An unexpected error occurred';
            setTimeout(() => {
              if (isMountedRef.current && !signal.aborted) {
                setProgress(prev => ({
                  ...prev,
                  [url]: { status: 'error', index: index + 1, total: urls.length, error: errorMsg }
                }));
              }
            }, 0);
            return {
              url,
              data: null,
              success: false,
              error: errorMsg
            };
          }
        });

        // Wait for all promises to complete and update results as they finish
        // Use Promise.allSettled to ensure all promises complete even if some fail
        const results = await Promise.allSettled(scrapingPromises);

        if (!isMountedRef.current || signal.aborted) {
          setIsProcessing(false);
          return;
        }

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value) {
            newResults.push(result.value);
          } else if (result.status === 'rejected') {
            const errorResult = {
              url: '',
              data: null,
              success: false,
              error: result.reason?.message || 'An unexpected error occurred'
            };
            newResults.push(errorResult);
          }
        });

        // Batch final state updates
        if (isMountedRef.current && !signal.aborted) {
          requestAnimationFrame(() => {
            if (isMountedRef.current && !signal.aborted) {
              setResults(newResults);
              setIsProcessing(false);
              if (onComplete) {
                onComplete(newResults);
              }
            }
          });
        }
      }, 0); // Run in next event loop tick to avoid blocking
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return (
          <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'success':
        return (
          <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const completedCount = Object.values(progress).filter(p => p.status === 'success' || p.status === 'error').length;
  const successCount = Object.values(progress).filter(p => p.status === 'success').length;
  const totalCount = urls.length;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Scraping and Generating</h2>
        <p className="text-gray-600">Processing doctor pages and extracting information...</p>
      </div>

      {/* Progress Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-blue-900">Progress</h3>
          <span className="text-blue-700 font-medium">
            {completedCount} / {totalCount} completed
          </span>
        </div>
        <div className="w-full bg-blue-200 rounded-full h-4 mb-2">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-300"
            style={{ width: `${(completedCount / totalCount) * 100}%` }}
          ></div>
        </div>
        <div className="text-sm text-blue-700">
          {successCount} successful, {completedCount - successCount} failed
        </div>
      </div>

      {/* URL Processing List */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h3>
        <div className="space-y-3">
          {urls.map((url, index) => {
            const urlProgress = progress[url] || { status: 'pending', index: index + 1, total: totalCount };
            return (
              <div
                key={index}
                className={`flex items-center justify-between p-4 border rounded-md ${
                  urlProgress.status === 'success' ? 'bg-green-50 border-green-200' :
                  urlProgress.status === 'error' ? 'bg-red-50 border-red-200' :
                  urlProgress.status === 'processing' ? 'bg-blue-50 border-blue-200' :
                  'bg-white border-gray-200'
                }`}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="mr-3">
                    {getStatusIcon(urlProgress.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      [{urlProgress.index}/{urlProgress.total}] {url}
                    </div>
                    {urlProgress.error && (
                      <div className="text-xs text-red-600 mt-1 font-medium">
                        Error: {urlProgress.error}
                      </div>
                    )}
                    {urlProgress.status === 'success' && (
                      <div className="text-xs text-green-600 mt-1 font-medium">✓ Successfully extracted</div>
                    )}
                    {urlProgress.status === 'processing' && (
                      <div className="text-xs text-blue-600 mt-1">Processing...</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">{error}</div>
        </div>
      )}

      {/* Summary of Errors */}
      {completedCount === totalCount && successCount < totalCount && successCount > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-yellow-800">
            <strong>Warning:</strong> {totalCount - successCount} out of {totalCount} URL{totalCount !== 1 ? 's' : ''} failed to scrape.
            {' '}You can still proceed with the {successCount} successful one{successCount !== 1 ? 's' : ''}.
          </div>
        </div>
      )}

      {/* All Failed Message */}
      {completedCount === totalCount && successCount === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800 font-medium">
            <strong>Error:</strong> All URLs failed to scrape. Please check the error messages above and try again.
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      {completedCount === totalCount && (
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
          >
            ← Back
          </button>
          <button
            onClick={() => onComplete(results)}
            disabled={successCount === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {successCount === 0
              ? 'No Data to Preview'
              : `Continue to Preview (${successCount} doctor${successCount !== 1 ? 's' : ''}) →`}
          </button>
        </div>
      )}
    </div>
  );
};

export default Step2_Scraping;

